import { z } from 'zod';
import { CargoImportFileFormatSchema } from './enums.js';

// Cargo file wire skeletons (#1227). FOUNDATION ONLY — basic entity
// projections consistent with the Prisma `CargoFile` (import file) and
// `CargoPaymentDocument` models. Import-result counters are NOT modelled
// (the source records no import outcome; #1229 owns any net-new counters).

/**
 * @aiPurpose Cargo import-file entity skeleton (#1227). One uploaded Yan /
 *   full-truck workbook. `processed` is a boolean (source `status` bool); the
 *   parsing pipeline lands in #1229. `objectKey` references a
 *   `cargo-import-file` storage object.
 * @aiInvariant FOUNDATION skeleton — not `.strict()`; #1229 may extend it.
 */
export const CargoImportFileSchema = z.object({
  id: z.string().uuid(),
  legacySourceId: z.string().nullable(),
  format: CargoImportFileFormatSchema,
  objectKey: z.string(),
  processed: z.boolean(),
  uploadedById: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type CargoImportFile = z.infer<typeof CargoImportFileSchema>;

/**
 * @aiPurpose Cargo client-payment document entity skeleton (#1227). One
 *   payment proof file attached to a registry article. In the source the
 *   proof file and its payment `amount` («Деньги в платежки») live in one
 *   row; `balance` carries the running balance. Both money figures are
 *   nullable 2-decimal strings on the wire (backend `Decimal(12,2)`).
 *   `objectKey` references a `cargo-payment-doc` storage object.
 * @aiInvariant FOUNDATION skeleton — not `.strict()`; #1230 may extend it.
 */
export const CargoPaymentDocumentSchema = z.object({
  id: z.string().uuid(),
  legacySourceId: z.string().nullable(),
  cargoId: z.string().uuid(),
  objectKey: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  contentLength: z.number().int().nonnegative(),
  amount: z.string().nullable(),
  balance: z.string().nullable(),
  uploadedById: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  // Soft-delete (#1227, net-new vs source): a deleted doc keeps its history.
  deletedAt: z.string().datetime({ offset: true }).nullable(),
  deletedById: z.string().uuid().nullable(),
});
export type CargoPaymentDocument = z.infer<typeof CargoPaymentDocumentSchema>;
